import { useState } from 'react';
import { Ellipsis } from 'lucide-react';
import { Link, LinkProps } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu.tsx';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/Drawer.tsx';
import { useIsMobile } from '@/hooks/use-mobile.tsx';

export interface RowActionsMenuItem {
  label: string;
  onSelect?: () => void;
  link?: Pick<LinkProps, 'to' | 'params' | 'search'>;
  destructive?: boolean;
}

interface RowActionsMenuProps {
  /** Nazwa wiersza — bez niej czytnik ekranu słyszy same „Akcje” przy każdym wierszu. */
  label: string;
  items: RowActionsMenuItem[];
}

const drawerItemClass =
  'flex w-full items-center rounded-md px-3 py-3 text-left text-sm font-medium text-base-text hover:bg-neutral-50';

/** Menu wiersza spod wielokropka; poniżej `md` arkusz od dołu jak `MobileRowActions`. */
const RowActionsMenu = ({ label, items }: RowActionsMenuProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;

  const trigger = (
    <ActionButton
      tone="ghost"
      size="xs"
      iconOnly
      aria-label={`Akcje: ${label}`}
      // Arkusz ukrywa resztę strony przez `aria-hidden`; przycisk nie może wtedy trzymać fokusu.
      onClick={(event) => event.currentTarget.blur()}
      className="text-neutral-600"
    >
      <Ellipsis aria-hidden />
    </ActionButton>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent
          aria-describedby={undefined}
          className="rounded-t-[22px] border-t-0 bg-basic-white"
        >
          <DrawerTitle className="sr-only">Akcje: {label}</DrawerTitle>
          <ul className="flex flex-col gap-0.5 px-2 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
            {items.map((item) => (
              <li key={item.label}>
                {item.link ? (
                  <Link
                    to={item.link.to}
                    params={item.link.params}
                    search={item.link.search}
                    onClick={() => setOpen(false)}
                    className={cn(
                      drawerItemClass,
                      'no-underline',
                      item.destructive && 'text-error-200',
                    )}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      item.onSelect?.();
                      setOpen(false);
                    }}
                    className={cn(
                      drawerItemClass,
                      item.destructive && 'text-error-200',
                    )}
                  >
                    {item.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((item) =>
          item.link ? (
            <DropdownMenuItem
              key={item.label}
              asChild
              className={cn(item.destructive && 'text-error-200')}
            >
              <Link
                to={item.link.to}
                params={item.link.params}
                search={item.link.search}
                className="no-underline"
              >
                {item.label}
              </Link>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              key={item.label}
              onSelect={item.onSelect}
              className={cn(item.destructive && 'text-error-200')}
            >
              {item.label}
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RowActionsMenu;
